import React, { useState, useEffect, useCallback } from 'react';
import TextAd from './TextAd';
import BannerAd from './BannerAd';
import './StorefrontAd.css';

/**
 * AdPlacement - Smart wrapper that fetches and displays ads by placement
 *
 * Placements:
 * - HOMEPAGE_HERO: Text ad in hero section
 * - HOMEPAGE_BANNER: Banner ad below hero
 * - SHOP_INLINE: Inline ad in product grid
 * - SHOP_SIDEBAR: Sidebar ad
 * - CATEGORY_INLINE: Inline ad in category sections
 * - ABOUT_BANNER: Banner on about page
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Placement to class mapping
const PLACEMENT_CLASSES = {
  'HOMEPAGE_HERO': 'ad-placement-hero',
  'HOMEPAGE_BANNER': 'ad-placement-homepage-banner',
  'SHOP_INLINE': 'ad-placement-shop-inline',
  'SHOP_SIDEBAR': 'ad-placement-shop-sidebar',
  'CATEGORY_INLINE': 'ad-placement-category-inline',
  'ABOUT_BANNER': 'ad-placement-about-banner'
};

const AdPlacement = ({
  storeSlug,
  placement,
  className = '',
  showAdvertiseBlurb = false,
  maxAds = 1,
  showPlaceholder = false
}) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advertiseBlurb, setAdvertiseBlurb] = useState(null);
  const [error, setError] = useState(null);

  // Fetch ads for this placement
  useEffect(() => {
    if (!storeSlug || !placement) {
      setLoading(false);
      return;
    }

    const fetchAds = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_BASE}/api/storefront/ads/${storeSlug}/placement/${placement}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch ads');
        }

        const data = await response.json();

        if (data.success) {
          setAds(data.ads?.slice(0, maxAds) || []);
          if (data.advertiseBlurb) {
            setAdvertiseBlurb(data.advertiseBlurb);
          }
        }
      } catch (err) {
        console.error('[AdPlacement] Error fetching ads:', err);
        setError(err.message);
        setAds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [storeSlug, placement, maxAds]);

  // Track view
  const handleView = useCallback(async (adId) => {
    try {
      await fetch(`${API_BASE}/api/storefront/ads/view/${adId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('[AdPlacement] Error tracking view:', err);
    }
  }, []);

  // Track click and redirect with UTM parameters
  const handleClick = useCallback(async (adId, ctaLink) => {
    let redirectUrl = ctaLink;

    try {
      const response = await fetch(`${API_BASE}/api/storefront/ads/click/${adId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrer: document.referrer || null,
          pageUrl: window.location.href
        })
      });

      const data = await response.json();

      // Use the tracked URL with UTM parameters from backend
      if (data.success && data.redirectUrl) {
        redirectUrl = data.redirectUrl;
      }
    } catch (err) {
      console.error('[AdPlacement] Error tracking click:', err);
      // Fall back to original link if tracking fails
    }

    // Navigate to link
    if (redirectUrl) {
      // Check if external link
      if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = redirectUrl;
      }
    }
  }, []);

  // Loading state
  if (loading && showPlaceholder) {
    const isTextPlacement = ['HOMEPAGE_HERO', 'SHOP_SIDEBAR'].includes(placement);
    return (
      <div className={`ad-placement ${PLACEMENT_CLASSES[placement] || ''} ${className}`}>
        <div className={`ad-placeholder ${isTextPlacement ? 'ad-placeholder-text' : 'ad-placeholder-banner'}`} />
      </div>
    );
  }

  // No ads
  if (!ads || ads.length === 0) {
    // Show advertise blurb if enabled and no ads
    if (showAdvertiseBlurb && advertiseBlurb) {
      return (
        <div className={`ad-placement ${PLACEMENT_CLASSES[placement] || ''} ${className}`}>
          <AdvertiseBlurb blurb={advertiseBlurb} storeSlug={storeSlug} />
        </div>
      );
    }
    return null;
  }

  const placementClass = PLACEMENT_CLASSES[placement] || '';

  return (
    <div className={`ad-placement ${placementClass} ${className}`}>
      {ads.map((ad, index) => (
        <React.Fragment key={ad.id || index}>
          {ad.type === 'TEXT' ? (
            <TextAd
              ad={ad}
              onView={handleView}
              onClick={handleClick}
            />
          ) : (
            <BannerAd
              ad={ad}
              onView={handleView}
              onClick={handleClick}
            />
          )}
        </React.Fragment>
      ))}

      {/* Advertise blurb after ads */}
      {showAdvertiseBlurb && advertiseBlurb && (
        <AdvertiseBlurb blurb={advertiseBlurb} storeSlug={storeSlug} />
      )}
    </div>
  );
};

/**
 * AdvertiseBlurb - "Advertise with us" link
 */
const AdvertiseBlurb = ({ blurb, storeSlug }) => {
  const handleClick = () => {
    if (blurb.contactEmail) {
      window.location.href = `mailto:${blurb.contactEmail}?subject=Advertising Inquiry`;
    }
  };

  return (
    <button
      className="advertise-blurb"
      onClick={handleClick}
      type="button"
    >
      <svg
        className="advertise-blurb-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m3 11 18-5v12L3 14v-3z"/>
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
      </svg>
      {blurb.text || 'Advertise with us!'}
    </button>
  );
};

/**
 * useStorefrontAds - Hook to fetch all ads for a store
 */
export const useStorefrontAds = (storeSlug) => {
  const [ads, setAds] = useState({});
  const [loading, setLoading] = useState(true);
  const [advertiseBlurb, setAdvertiseBlurb] = useState(null);

  useEffect(() => {
    if (!storeSlug) {
      setLoading(false);
      return;
    }

    const fetchAllAds = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${API_BASE}/api/storefront/ads/${storeSlug}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch ads');
        }

        const data = await response.json();

        if (data.success) {
          setAds(data.ads || {});
          if (data.advertiseBlurb) {
            setAdvertiseBlurb(data.advertiseBlurb);
          }
        }
      } catch (err) {
        console.error('[useStorefrontAds] Error:', err);
        setAds({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllAds();
  }, [storeSlug]);

  return { ads, loading, advertiseBlurb };
};

export default AdPlacement;
